import React, { createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { getItemFromCollection } from "directus/src/operations";

const QuadContext = createContext();

export const useQuad = () => {
  const context = useContext(QuadContext);
  if (!context) {
    throw new Error("useQuad must be used within a QuadProvider");
  }
  return context;
};

export const QuadProvider = ({ children, quadId }) => {
  const {
    data: quad = null,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["quad", quadId],
    queryFn: async () => {
      if (!quadId) return null;
      
      const query = {
        fields: ["id", "name", "status", "start_date", "end_date", "allow_self_nomination"],
      };
      
      return await getItemFromCollection("performance_review_months", quadId, query);
    },
    enabled: !!quadId,
  });

  // Utility functions
  const isQuadCompleted = () => {
    return quad?.status === "completed";
  };

  const isQuadOngoing = () => {
    return quad?.status === "ongoing";
  };

  const shouldDisableEditing = () => {
    return isQuadCompleted();
  };

  const getQuadStatusLabel = () => {
    if (!quad) return "Unknown";
    return quad.status?.charAt(0).toUpperCase() + quad.status?.slice(1) || "Unknown";
  };

  const value = {
    quad,
    quadId,
    isLoading,
    error,
    isQuadCompleted,
    isQuadOngoing,
    shouldDisableEditing,
    getQuadStatusLabel,
    canSelfNominate: quad?.allow_self_nomination ?? false,
  };

  return (
    <QuadContext.Provider value={value}>
      {children}
    </QuadContext.Provider>
  );
};
